import { Match } from '../../types';
import { GroupStandingRow, MundialTeam, MUNDIAL_LEAGUE } from '../../types/mundial';

const POINTS_WIN = 3;
const POINTS_DRAW = 1;
const POINTS_LOSS = 0;

function emptyStanding(team: MundialTeam): Omit<GroupStandingRow, 'position'> {
  return {
    teamCode: team.code,
    teamName: team.name,
    flagUrl: team.flag_url,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    goalDifference: 0,
    points: 0,
  };
}

function compareStandings(
  a: GroupStandingRow,
  b: GroupStandingRow,
  orderByCode: Map<string, number>
): number {
  if (b.points !== a.points) return b.points - a.points;
  if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
  if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
  const orderA = orderByCode.get(a.teamCode) ?? Number.MAX_SAFE_INTEGER;
  const orderB = orderByCode.get(b.teamCode) ?? Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) return orderA - orderB;
  return a.teamName.localeCompare(b.teamName);
}

/**
 * Calcula la tabla de un grupo a partir de partidos con resultado.
 * Victoria 3 pts, empate 1, derrota 0. Orden: pts → dif → GF.
 */
export function calculateGroupStandings(
  groupTeams: MundialTeam[],
  groupMatches: Match[]
): GroupStandingRow[] {
  const orderedTeams = [...groupTeams].sort(
    (a, b) => (a.sort_order ?? Number.MAX_SAFE_INTEGER) - (b.sort_order ?? Number.MAX_SAFE_INTEGER)
  );
  const orderByCode = new Map(
    orderedTeams.map((t, i) => [t.code.toUpperCase(), t.sort_order ?? i])
  );

  const standingsMap = new Map<string, Omit<GroupStandingRow, 'position'>>();
  orderedTeams.forEach((t) => standingsMap.set(t.code.toUpperCase(), emptyStanding(t)));

  const completed = groupMatches.filter(
    (m) =>
      m.league === MUNDIAL_LEAGUE &&
      m.match_type === 'group' &&
      m.score_a !== null &&
      m.score_b !== null
  );

  completed.forEach((match) => {
    const codeA = (match.team_a_code || '').toUpperCase();
    const codeB = (match.team_b_code || '').toUpperCase();
    const rowA = standingsMap.get(codeA);
    const rowB = standingsMap.get(codeB);
    if (!rowA || !rowB || match.score_a === null || match.score_b === null) return;

    const ga = match.score_a;
    const gb = match.score_b;

    rowA.played += 1;
    rowB.played += 1;
    rowA.goalsFor += ga;
    rowA.goalsAgainst += gb;
    rowB.goalsFor += gb;
    rowB.goalsAgainst += ga;

    if (ga > gb) {
      rowA.won += 1;
      rowA.points += POINTS_WIN;
      rowB.lost += 1;
      rowB.points += POINTS_LOSS;
    } else if (ga < gb) {
      rowB.won += 1;
      rowB.points += POINTS_WIN;
      rowA.lost += 1;
      rowA.points += POINTS_LOSS;
    } else {
      rowA.drawn += 1;
      rowB.drawn += 1;
      rowA.points += POINTS_DRAW;
      rowB.points += POINTS_DRAW;
    }

    rowA.goalDifference = rowA.goalsFor - rowA.goalsAgainst;
    rowB.goalDifference = rowB.goalsFor - rowB.goalsAgainst;
  });

  const rows = Array.from(standingsMap.values()).map((r) => ({ ...r })) as GroupStandingRow[];
  rows.sort((a, b) => compareStandings(a, b, orderByCode));
  return rows.map((row, index) => ({ ...row, position: index + 1 }));
}

export function isGroupStageComplete(
  groupTeams: MundialTeam[],
  groupMatches: Match[]
): boolean {
  if (groupTeams.length < 4) return false;
  const expectedMatches = 6;
  const withResult = groupMatches.filter(
    (m) => m.score_a !== null && m.score_b !== null
  ).length;
  return withResult >= expectedMatches;
}
